/**
 * ScreenRejected - Tela de Cadastro Rejeitado
 * Exibida quando o admin rejeita permanentemente o cadastro do motorista
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, XCircle, Shield, LogOut, RefreshCw } from 'lucide-react';

export function ScreenRejected() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [driverName, setDriverName] = useState<string>('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        async function checkStatus() {
            try {
                const { data: { user }, error: authError } = await supabase.auth.getUser();

                if (authError || !user) {
                    navigate('/driver/login', { replace: true });
                    return;
                }

                // Verificar o status do perfil
                const { data: driverData, error: driverError } = await supabase
                    .from('drivers')
                    .select('nome_completo, profile_status')
                    .eq('id', user.id)
                    .single();

                if (driverError) {
                    // Se não encontrou, provavelmente já foi deletado
                    if (driverError.code === 'PGRST116') {
                        await supabase.auth.signOut();
                        navigate('/driver/register', { replace: true });
                        return;
                    }
                    console.error('[ScreenRejected] Erro ao buscar driver:', driverError);
                    navigate('/driver/login', { replace: true });
                    return;
                }

                setDriverName(driverData.nome_completo || 'Motorista');

                // Redirecionar baseado no status
                if (driverData.profile_status === 'approved') {
                    navigate('/dash_companion', { replace: true });
                    return;
                }

                if (driverData.profile_status !== 'rejected') {
                    navigate('/driver/login', { replace: true });
                    return;
                }

                setIsLoading(false);
            } catch (error) {
                console.error('[ScreenRejected] Erro:', error);
                navigate('/driver/login', { replace: true });
            }
        }

        checkStatus();
    }, [navigate]);

    const handleDeleteAndLogout = async () => {
        try {
            setIsDeleting(true);

            const { data: { session } } = await supabase.auth.getSession();

            if (session) {
                console.log('[ScreenRejected] Iniciando exclusão completa da conta...');
                console.log('[ScreenRejected] User ID:', session.user.id);

                // Tentar primeiro a RPC (Database Function) - mais confiável que Edge Function
                console.log('[ScreenRejected] Chamando RPC delete_driver_account...');

                const { data: rpcData, error: rpcError } = await supabase.rpc('delete_driver_account');

                console.log('[ScreenRejected] Resposta da RPC:', { rpcData, rpcError });

                if (rpcError) {
                    console.error('[ScreenRejected] ❌ Erro na RPC:', rpcError);

                    // Fallback: Tentar Edge Function
                    console.log('[ScreenRejected] Tentando fallback com Edge Function...');

                    const { data: edgeData, error: edgeError } = await supabase.functions.invoke('delete-driver-account', {
                        headers: {
                            Authorization: `Bearer ${session.access_token}`
                        }
                    });

                    if (edgeError) {
                        console.error('[ScreenRejected] ❌ Edge Function também falhou:', edgeError);

                        // Último fallback: deletar apenas tabela drivers
                        console.log('[ScreenRejected] Tentando último fallback: deletar apenas tabela drivers...');
                        await supabase.from('drivers').delete().eq('id', session.user.id);
                        console.warn('[ScreenRejected] ⚠️ ATENÇÃO: A conta de autenticação pode NÃO ter sido deletada!');
                    } else {
                        console.log('[ScreenRejected] ✅ Conta deletada via Edge Function:', edgeData);
                    }
                } else {
                    // Verificar se a RPC retornou sucesso
                    if (rpcData && rpcData.success) {
                        console.log('[ScreenRejected] ✅ Conta deletada com sucesso via RPC:', rpcData);
                    } else {
                        console.error('[ScreenRejected] ❌ RPC retornou erro:', rpcData?.error);
                        // Fallback: deletar tabela drivers
                        await supabase.from('drivers').delete().eq('id', session.user.id);
                        console.warn('[ScreenRejected] ⚠️ Fallback: apenas tabela drivers deletada');
                    }
                }
            } else {
                console.warn('[ScreenRejected] Nenhuma sessão encontrada');
            }

            // Fazer logout - pode falhar com 403 se a conta já foi deletada (esperado)
            console.log('[ScreenRejected] Fazendo signOut...');
            try {
                await supabase.auth.signOut({ scope: 'local' }); // Usar scope local para evitar erro 403
            } catch (signOutError) {
                // Ignorar erro de signOut quando conta já foi deletada
                console.log('[ScreenRejected] SignOut ignorado (conta já deletada):', signOutError);
            }

            // Limpar qualquer storage residual
            localStorage.removeItem('sb-pvddhqfctgrsbfsnkqri-auth-token');
            sessionStorage.clear();

            console.log('[ScreenRejected] ✅ Processo completo, redirecionando...');
            navigate('/driver/register', { replace: true });
        } catch (error) {
            console.error('[ScreenRejected] Erro ao deletar:', error);
            // Em caso de erro, ainda limpar sessão local e redirecionar
            try {
                await supabase.auth.signOut({ scope: 'local' });
            } catch {
                // Ignorar
            }
            localStorage.removeItem('sb-pvddhqfctgrsbfsnkqri-auth-token');
            sessionStorage.clear();
            navigate('/driver/register', { replace: true });
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50/30 to-gray-50 flex items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-red-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50/30 to-gray-50 flex flex-col items-center justify-center p-4">
            {/* Container Principal */}
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <img
                        src="https://locaninja.com/logotipo-watch.png"
                        alt="Watch Logo"
                        className="h-12 mx-auto mb-4"
                    />
                </div>

                {/* Card Principal */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    {/* Header do Card */}
                    <div className="bg-gradient-to-r from-gray-600 to-gray-700 p-6">
                        <div className="flex items-center justify-center">
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                <XCircle className="w-8 h-8 text-white" />
                            </div>
                        </div>
                        <h1 className="text-xl font-bold text-white text-center mt-4">
                            Cadastro Não Aprovado
                        </h1>
                    </div>

                    {/* Conteúdo do Card */}
                    <div className="p-6">
                        {/* Saudação */}
                        <p className="text-gray-700 text-center mb-6">
                            Olá, <span className="font-semibold text-gray-900">{driverName}</span>
                        </p>

                        {/* Mensagem Principal */}
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
                            <p className="text-gray-700 text-sm leading-relaxed text-center">
                                Infelizmente, após análise cuidadosa, seu cadastro não foi aprovado neste momento. Agradecemos seu interesse em fazer parte da nossa plataforma.
                            </p>
                        </div>

                        {/* Informação sobre dados */}
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
                            <div className="flex items-start gap-3">
                                <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-blue-900 text-sm font-medium mb-1">
                                        Proteção dos seus dados
                                    </p>
                                    <p className="text-blue-700 text-xs leading-relaxed">
                                        Para sua segurança, seus dados pessoais serão removidos do nosso sistema automaticamente ao sair.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Oportunidades futuras */}
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 mb-6">
                            <div className="flex items-start gap-3">
                                <RefreshCw className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-emerald-900 text-sm font-medium mb-1">
                                        Novas oportunidades
                                    </p>
                                    <p className="text-emerald-700 text-xs leading-relaxed">
                                        Você poderá realizar um novo cadastro no futuro. Fique atento às nossas novidades e requisitos atualizados.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Botão de Ação */}
                        <button
                            onClick={handleDeleteAndLogout}
                            disabled={isDeleting}
                            className="w-full py-4 bg-gray-800 text-white font-semibold rounded-xl hover:bg-gray-900 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Processando...
                                </>
                            ) : (
                                <>
                                    <LogOut className="w-5 h-5" />
                                    Sair e Remover Meus Dados
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Texto de Rodapé */}
                <p className="text-center text-gray-400 text-xs mt-6">
                    Obrigado por seu interesse. Esperamos vê-lo novamente no futuro.
                </p>
            </div>
        </div>
    );
}

export default ScreenRejected;
